import { LuCircleHelp, LuEqualApproximately, LuLink2, LuListChecks, LuPercent, LuRocket } from 'react-icons/lu';

import type { HoldingsReport, PerformanceReport } from '@ndvr/reporting-api';

import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatCurrency, formatCurrencyShort, formatDate, formatPercent } from 'utils';

import { ALLOCATION_SEGMENT_COLORS } from '../asset-allocation-presentation';
import type { AssetAllocationSegmentKey } from '../asset-allocation-presentation';
import { getAssetAllocation } from '../holdings';
import { signColorClass } from '../reporting/format';

const assetClassNames: Record<AssetAllocationSegmentKey, string> = {
    equities: 'Equity',
    bonds: 'Fixed Income',
    cash: 'Cash',
    other: 'Other',
};

const formatReturn = (value?: string) => {
    if (value === undefined) return '—';

    const numericValue = Number(value);
    return `${numericValue > 0 ? '+' : ''}${formatPercent(numericValue, 1)}`;
};

const formatNextWithdrawal = (nextWithdrawal?: { date: string; amount: number }, isLoading = false) => {
    if (isLoading || !nextWithdrawal) return '—';
    return `${formatDate(nextWithdrawal.date, 'MMM d')} · ${formatCurrency(nextWithdrawal.amount)}`;
};

const formatBoostCurrency = (value: number) => formatCurrencyShort(value, 'USD', 1);

const formatYtdGains = (shortTerm?: number, longTerm?: number, isLoading = false) => {
    if (isLoading || (shortTerm === undefined && longTerm === undefined)) return '—';
    return `${shortTerm === undefined ? '—' : formatBoostCurrency(shortTerm)} ST · ${
        longTerm === undefined ? '—' : formatBoostCurrency(longTerm)
    } LT`;
};

interface HouseholdAccountsSummaryCardProps {
    managedValue: number;
    heldAwayValue: number;
    boostedValue: number;
    boostedAccountCount: number;
    accountCount: number;
    totalCashValue?: number;
    harvestableLossValue?: number;
    ytdShortTermGainsValue?: number;
    ytdLongTermGainsValue?: number;
    isYtdTaxMetricsLoading: boolean;
    nextWithdrawal?: { date: string; amount: number };
    isNextWithdrawalLoading: boolean;
    holdingsReport?: HoldingsReport | null;
    isHoldingsLoading: boolean;
    ytdPerformance?: PerformanceReport | null;
    isYtdPerformanceLoading: boolean;
    allTimePerformance?: PerformanceReport | null;
    isAllTimePerformanceLoading: boolean;
}

export const HouseholdAccountsSummaryCard = ({
    managedValue,
    heldAwayValue,
    boostedValue,
    boostedAccountCount,
    accountCount,
    totalCashValue,
    harvestableLossValue,
    ytdShortTermGainsValue,
    ytdLongTermGainsValue,
    isYtdTaxMetricsLoading,
    nextWithdrawal,
    isNextWithdrawalLoading,
    holdingsReport,
    isHoldingsLoading,
    ytdPerformance,
    isYtdPerformanceLoading,
    allTimePerformance,
    isAllTimePerformanceLoading,
}: HouseholdAccountsSummaryCardProps) => {
    const { total: holdingsTotal, segments: assetClasses } = holdingsReport
        ? getAssetAllocation(holdingsReport)
        : { total: 0, segments: [] };
    const portfolioBoostEnabled = boostedAccountCount > 0;
    const boostItems = [
        {
            label: 'Total Cash',
            value: totalCashValue === undefined ? '—' : formatBoostCurrency(totalCashValue),
            icon: LuListChecks,
            iconClassName: 'bg-lime-50 text-green-600',
        },
        {
            label: 'Harvestable losses',
            value: harvestableLossValue === undefined ? '—' : formatBoostCurrency(harvestableLossValue),
            icon: LuEqualApproximately,
            iconClassName: 'bg-blue-50 text-blue-700',
        },
        {
            label: 'YTD ST/LT Gains',
            value: formatYtdGains(ytdShortTermGainsValue, ytdLongTermGainsValue, isYtdTaxMetricsLoading),
            icon: LuPercent,
            iconClassName: 'bg-green-50 text-green-600',
        },
        {
            label: 'Next Planned Withdrawal',
            value: formatNextWithdrawal(nextWithdrawal, isNextWithdrawalLoading),
            icon: LuLink2,
            iconClassName: 'bg-cyan-50 text-cyan-600',
        },
    ];

    return (
        <div className="flex flex-col gap-4">
            <Card className="border-border grid overflow-hidden rounded-2xl border bg-white lg:grid-cols-[3fr_2fr]">
                <div className="p-4 lg:p-6">
                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <div className="text-cocoa-500 text-[10px] font-semibold tracking-wider uppercase">
                                Managed value
                            </div>
                            <div className="text-cocoa-1000 mt-2 text-xl font-semibold tabular-nums">
                                {formatCurrency(managedValue)}
                            </div>
                            <div className="mt-1.5 flex items-center gap-2 text-xs font-semibold">
                                <span className={signColorClass(ytdPerformance?.returnsPercentNet) ?? 'text-cocoa-500'}>
                                    {isYtdPerformanceLoading ? '—' : formatReturn(ytdPerformance?.returnsPercentNet)}{' '}
                                    YTD
                                </span>
                                <span className="text-cocoa-400">·</span>
                                <span
                                    className={
                                        signColorClass(allTimePerformance?.returnsPercentNet) ?? 'text-cocoa-500'
                                    }
                                >
                                    {isAllTimePerformanceLoading
                                        ? '—'
                                        : formatReturn(allTimePerformance?.returnsPercentNet)}{' '}
                                    All Time
                                </span>
                            </div>
                        </div>
                        <div>
                            <div className="text-cocoa-500 text-[10px] font-semibold tracking-wider uppercase">
                                Total household value
                            </div>
                            <div className="text-cocoa-1000 mt-2 text-xl font-semibold tabular-nums">
                                {formatCurrency(managedValue + heldAwayValue)}
                            </div>
                            <div className="text-cocoa-500 mt-1.5 text-xs font-medium">
                                {formatCurrency(heldAwayValue)} held away
                            </div>
                        </div>
                    </div>

                    {isHoldingsLoading ? (
                        <div className="bg-cocoa-100 mt-5 h-2 animate-pulse rounded-full" />
                    ) : (
                        <div className="mt-5 flex h-2 overflow-hidden rounded-full">
                            {assetClasses.map((assetClass) => (
                                <div
                                    key={assetClass.key}
                                    className={ALLOCATION_SEGMENT_COLORS[assetClass.key]}
                                    style={{ width: `${(assetClass.amount / holdingsTotal) * 100}%` }}
                                />
                            ))}
                        </div>
                    )}

                    <div className="mt-3 overflow-x-auto">
                        <Table className="text-xs">
                            <TableHeader>
                                <TableRow className="hover:bg-transparent">
                                    <TableHead className="text-cocoa-500 h-auto min-w-40 py-2 text-[10px] tracking-wider uppercase">
                                        Asset class
                                    </TableHead>
                                    {['Value', 'Current'].map((heading) => (
                                        <TableHead
                                            key={heading}
                                            className="text-cocoa-500 h-auto py-2 text-right text-[10px] tracking-wider uppercase"
                                        >
                                            {heading}
                                        </TableHead>
                                    ))}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isHoldingsLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={3} className="text-cocoa-400 py-5 text-center">
                                            Loading allocation…
                                        </TableCell>
                                    </TableRow>
                                ) : assetClasses.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={3} className="text-cocoa-400 py-5 text-center">
                                            No holdings available
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    assetClasses.map((assetClass) => {
                                        const weight = (assetClass.amount / holdingsTotal) * 100;

                                        return (
                                            <TableRow key={assetClass.key}>
                                                <TableCell className="text-cocoa-900 py-2">
                                                    <div className="flex items-center gap-3">
                                                        <span
                                                            className={`h-3.5 w-3.5 rounded-sm ${ALLOCATION_SEGMENT_COLORS[assetClass.key]}`}
                                                        />
                                                        {assetClassNames[assetClass.key]}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-cocoa-1000 py-2 text-right font-semibold tabular-nums">
                                                    {formatCurrencyShort(assetClass.amount)}
                                                </TableCell>
                                                <TableCell className="text-cocoa-1000 py-2 text-right font-semibold tabular-nums">
                                                    {formatPercent(weight, 1)}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </div>

                <div className="border-border border-t p-4 lg:border-t-0 lg:border-l lg:p-8">
                    <div className="flex items-center gap-3">
                        <LuRocket className={`h-4 w-4 ${portfolioBoostEnabled ? 'text-teal-700' : 'text-cocoa-300'}`} />
                        <div className="text-cocoa-500 text-[10px] font-semibold tracking-wider uppercase">
                            Portfolio Boost
                        </div>
                        {!portfolioBoostEnabled && (
                            <LuCircleHelp
                                className="text-cocoa-400 ml-auto h-4 w-4"
                                aria-label="Learn about Portfolio Boost"
                            />
                        )}
                    </div>
                    <div
                        className={`mt-2 text-xl font-semibold tabular-nums ${
                            portfolioBoostEnabled ? 'text-cocoa-1000' : 'text-teal-700'
                        }`}
                    >
                        {portfolioBoostEnabled ? formatCurrency(boostedValue) : 'Add Boost'}
                    </div>
                    <div className="text-cocoa-500 mt-1.5 text-xs font-medium">
                        {boostedAccountCount} of {accountCount} accounts managed
                    </div>

                    <div className="mt-5 space-y-3">
                        {boostItems.map(({ label, value, icon: Icon, iconClassName }) => (
                            <div key={label} className="flex items-center gap-3">
                                <span
                                    className={`flex h-6 w-6 items-center justify-center rounded-md ${
                                        portfolioBoostEnabled ? iconClassName : 'bg-cocoa-100 text-cocoa-300'
                                    }`}
                                >
                                    <Icon className="h-3.5 w-3.5" />
                                </span>
                                <span className="text-cocoa-700 text-sm">{label}</span>
                                <span className="text-cocoa-1000 ml-auto text-sm font-semibold tabular-nums">
                                    {value}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </Card>

            {accountCount > 0 && !portfolioBoostEnabled && (
                <div className="flex items-start gap-3 rounded-lg border border-cyan-200 bg-cyan-50 px-5 py-4">
                    <LuRocket className="mt-0.5 h-5 w-5 shrink-0 text-teal-700" />
                    <div>
                        <div className="text-cocoa-1000 text-sm font-medium">No accounts are Boosted yet.</div>
                        <div className="mt-1 text-sm leading-5 text-teal-800">
                            Enrolling puts the Portfolio Engine in charge of rebalancing, billing cash, and loss
                            harvesting for this household.
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
